/**
 * Auth Layout Component
 * 
 * Layout for authentication pages (login, register).
 * Uses same header/footer as PublicLayout for consistency.
 */

import { Outlet, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../common/LanguageSelector';

const AuthLayout = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-violet-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Header - same style as PublicLayout */}
      <header className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-xl sm:text-2xl font-display font-bold text-violet-600 shrink-0">
            <img src="/budgira.svg" alt="Budgira" className="w-7 h-7 sm:w-8 sm:h-8" />
            Budgira
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Hide login link on mobile since we're already on auth pages */}
            <Link
              to="/auth/login"
              className="hidden sm:block text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
            >
              {t('auth.login.submit')}
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Main content - centered for auth forms */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Outlet />
        </div>
      </main>

      {/* Footer - same style as PublicLayout */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              © {new Date().getFullYear()} Budgira. {t('footer.copyright')}
            </p>
            <nav className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm">
              <Link
                to="/about"
                className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                {t('footer.about')}
              </Link>
              <Link
                to="/contact"
                className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                {t('footer.contact')}
              </Link>
              <Link
                to="/terms"
                className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                {t('footer.terms')}
              </Link>
              <Link
                to="/privacy"
                className="text-slate-500 dark:text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                {t('footer.privacy')}
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthLayout;
